import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

// API/Components
import axiosInstance from '../components/axios';
import { STATE_COLOR, BATTERY_TYPE, formatDateTime, formatDate, invalidValueCheck } from './enums';
import { BuildDataGridCell, GetAnalysisIcon } from './ui_helpers';
import NoItemsOverlay from './no_items_overlay';
import './locations_table.css';

// Material UI
import {
	DataGrid,
	GridToolbarContainer,
	GridToolbarColumnsButton,
	GridToolbarFilterButton,
	GridToolbarExport,
	GridToolbarDensitySelector,
} from '@mui/x-data-grid';


// Locations List table columns
const columns = [
	{
		field: 'overall_system_analysis',
		headerName: 'Analysis',
		headerAlign: "left",
		headerClassName: 'hidden-header' ,
		description: 'Overall System Analysis status',
		minWidth: 50,
		flex: 1,
		filterable: false,
		sortable: false,
		renderCell: (params) => BuildDataGridCell('', 'center', null, false, GetAnalysisIcon(params.value)),
	},
	{
		field: 'location_name',
		headerName: 'Name',
		headerAlign: "left",
		headerClassName: 'wrap-header-text' ,
		description: 'Location Name',
		minWidth: 250,
		flex: 1,
		renderCell: (params) => BuildDataGridCell(params.value, 'left', null, false),
	},
	{
		field: 'serial_number',
		headerName: 'Serial #',
		headerAlign: "left",
		headerClassName: 'wrap-header-text' ,
		description: 'Serial Number',
		minWidth: 75,
		flex: 1,
		renderCell: (params) => BuildDataGridCell(params.value, 'left', null, false),
	},
	{
		field: 'bvs_model',
		headerName: 'Model',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'BVS Controller Model',
		minWidth: 100,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => BuildDataGridCell(params.value, 'center', null, false),
	},
	{
		field: 'organization',
		headerName: 'Organization',
		headerAlign: "left",
		headerClassName: 'wrap-header-text' ,
		description: 'Organization Hierarchy',
		minWidth: 150,
		flex: 1,
		renderCell: (params) => BuildDataGridCell(params.value, 'left', null, false),
	},
	{
		field: 'region',
		headerName: 'Region',
		headerAlign: "left",
		headerClassName: 'wrap-header-text' ,
		description: 'Region Hierarchy',
		minWidth: 150,
		flex: 1,
		renderCell: (params) => BuildDataGridCell(params.value, 'left', null, false),
	},
	{
		field: 'facility',
		headerName: 'Facility',
		headerAlign: "left",
		headerClassName: 'wrap-header-text' ,
		description: 'Facility Hierarchy',
		minWidth: 150,
		flex: 1,
		renderCell: (params) => BuildDataGridCell(params.value, 'left', null, false),
	},
	{
		field: 'building',
		headerName: 'Building',
		headerAlign: "left",
		headerClassName: 'wrap-header-text' ,
		description: 'Building Hierarchy',
		minWidth: 150,
		flex: 1,
		renderCell: (params) => BuildDataGridCell(params.value, 'left', null, false),
	},
	{
		field: 'num_strings',
		headerName: '# Strings',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'Number of Strings in the System',
		minWidth: 75,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => BuildDataGridCell(params.value, 'center', null, false),
	},
	{
		field: 'num_units_per_string',
		headerName: 'Units per String',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'Number of Units per String in the System',
		minWidth: 75,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => BuildDataGridCell(params.value, 'center', null, false),
	},
	{
		field: 'temp_sensors',
		headerName: '# Temp Sensors',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'Number of Temperature Sensors in the System',
		minWidth: 100,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => BuildDataGridCell(params.value, 'center', null, false),
	},
	{
		field: 'num_units_crit',
		headerName: '# Units Crit',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'Number of Units exceeding Critical Limits in the System',
		minWidth: 110,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => BuildDataGridCell(
			invalidValueCheck(params.value, '--'),
			'center',
			(params.value > 0) ? STATE_COLOR.CRITICAL : null,
			false
		),
	},
	{
		field: 'num_units_maint',
		headerName: '# Units Maint',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'Number of Units past Maintenance Limits in the System',
		minWidth: 110,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => BuildDataGridCell(
			invalidValueCheck(params.value, '--'),
			'center',
			(params.value > 0) ? STATE_COLOR.MAINTENANCE : null,
			false
		),
	},
	{
		field: 'batt_type',
		headerName: 'Battery Type',
		headerAlign: "left",
		headerClassName: 'wrap-header-text',
		description: 'Type of Battery',
		minWidth: 150,
		flex: 1,
		sortable: false,
		filterable: false,
		valueGetter: (params) => BATTERY_TYPE[params.value],
		renderCell: (params) => BuildDataGridCell(params.value, 'left', null, false),
	},
	{
		field: 'batt_model',
		headerName: 'Battery Model',
		headerAlign: "left",
		headerClassName: 'wrap-header-text' ,
		description: 'The exact battery model',
		minWidth: 275,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => BuildDataGridCell(params.value, 'left', null, false),
	},
	{
		field: 'controller_ip',
		headerName: 'IP',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The BVS Controller\'s IP address',
		minWidth: 140,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => BuildDataGridCell(invalidValueCheck(params.value, '--.--.--.--'), 'right', null, false),
	},
	{
		field: 'network_port',
		headerName: 'Port',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The BVS Controller\'s IP Port number',
		minWidth: 65,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => BuildDataGridCell(invalidValueCheck(params.value, '----'), 'center', null, false),
	},
	{
		field: 'controller_gateway',
		headerName: 'Gateway',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The BVS Controller\'s Gateway IP Address',
		minWidth: 140,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => BuildDataGridCell(invalidValueCheck(params.value, '--.--.--.--'), 'center', null, false),
	},
	{
		field: 'controller_mac',
		headerName: 'MAC Address',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The BVS Controller\'s MAC Address',
		minWidth: 175,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => BuildDataGridCell(invalidValueCheck(params.value, '--:--:--:--:--:--'), 'center', null, false),
	},
	{
		field: 'poll_interval',
		headerName: 'Poll Interval',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The interval that Observer polls the BVS Controller',
		minWidth: 120,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => BuildDataGridCell(invalidValueCheck(params.value, '--:--:--'), 'right', null, false),
	},
	{
		field: 'last_poll',
		headerName: 'Last Poll Date',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The date and time that Observer last polled the BVS Controller',
		minWidth: 210,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => BuildDataGridCell(formatDateTime(params.value), 'right', null, false),
	},
	{
		field: 'most_recent_measurement',
		headerName: 'Last Measurement',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The date and time of the most recent measurement',
		minWidth: 210,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => BuildDataGridCell(formatDateTime(params.value), 'right', null, false),
	},
	{
		field: 'most_recent_event',
		headerName: 'Most Recent Event',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The date and time of the most recent alarm',
		minWidth: 210,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => BuildDataGridCell(formatDateTime(params.value), 'right', null, false),
	},
	{
		field: 'num_recent_events',
		headerName: '# Recent Events',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The total number of alarms since the previous measurement',
		minWidth: 110,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => BuildDataGridCell(invalidValueCheck(params.value, '---'), 'right', null, false),
	},
	{
		field: 'num_recent_discharges',
		headerName: '# Recent Discharges',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The total number of discharge events since the previous measurement',
		minWidth: 110,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => BuildDataGridCell(invalidValueCheck(params.value, '---'), 'center', null, false),
	},
	{
		field: 'previous_measurement',
		headerName: 'Previous Measurement',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The date and time of the previous measurement',
		minWidth: 210,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => BuildDataGridCell(formatDateTime(params.value), 'right', null, false),
	},
	{
		field: 'risk_index',
		headerName: 'Risk Index',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The overall Risk Index of the system',
		minWidth: 100,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => {
			const text = invalidValueCheck(params.value, '--');
			const align = (params.value === 'Inoperable') ? 'center' : 'right';
			const color = (params.value === 'Inoperable') ? STATE_COLOR.CRITICAL : null;
			return BuildDataGridCell(text, align, color, false);
		},
	},
	{
		field: 'num_strings_missing_meas',
		headerName: '# Strings Missing Meas',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The number of strings in the system that are missing measurements',
		minWidth: 120,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => BuildDataGridCell(
			invalidValueCheck(params.value, '--'),
			'center',
			(params.value > 0) ? STATE_COLOR.CRITICAL : null,
			false
		),
	},
	{
		field: 'num_volt_upper_crit',
		headerName: '# Unit Volt Upper Crit',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The number of units with voltages exceeding Upper Critical Limits',
		minWidth: 120,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => BuildDataGridCell(
			invalidValueCheck(params.value, '--'),
			'center',
			(params.value > 0) ? STATE_COLOR.CRITICAL : null,
			false
		),
	},
	{
		field: 'num_volt_upper_maint',
		headerName: '# Unit Volt Upper Maint',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The number of units with voltages exceeding Upper Maintenance Limits',
		minWidth: 120,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => BuildDataGridCell(
			invalidValueCheck(params.value, '--'),
			'center',
			(params.value > 0) ? STATE_COLOR.MAINTENANCE : null,
			false
		),
	},
	{
		field: 'num_volt_lower_maint',
		headerName: '# Unit Volt Lower Maint',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The number of units with voltages below Lower Maintenance Limits',
		minWidth: 120,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => BuildDataGridCell(
			invalidValueCheck(params.value, '--'),
			'center',
			(params.value > 0) ? STATE_COLOR.MAINTENANCE : null,
			false
		),
	},
	{
		field: 'num_volt_lower_crit',
		headerName: '# Unit Volt Lower Crit',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The number of units with voltages below Lower Critical Limits',
		minWidth: 120,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => BuildDataGridCell(
			invalidValueCheck(params.value, '--'),
			'center',
			(params.value > 0) ? STATE_COLOR.CRITICAL : null,
			false
		),
	},
	{
		field: 'num_discharge_crit',
		headerName: '# Unit Crit Discharge',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The number of units with voltages below Critical Limits during a recent discharge event',
		minWidth: 120,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => BuildDataGridCell(
			invalidValueCheck(params.value, '--'),
			'center',
			(params.value > 0) ? STATE_COLOR.CRITICAL : null,
			false
		),
	},
	{
		field: 'num_discharge_maint',
		headerName: '# Unit Maint Discharge',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The number of units with voltages below Maintenance Limits during a recent discharge event',
		minWidth: 120,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => BuildDataGridCell(
			invalidValueCheck(params.value, '--'),
			'center',
			(params.value > 0) ? STATE_COLOR.MAINTENANCE : null,
			false
		),
	},
	{
		field: 'num_imped_crit',
		headerName: '# Unit Imped Crit',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The number of units with impedances exceeding Critical Limits',
		minWidth: 120,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => BuildDataGridCell(
			invalidValueCheck(params.value, '--'),
			'center',
			(params.value > 0) ? STATE_COLOR.CRITICAL : null,
			false
		),
	},
	{
		field: 'num_imped_maint',
		headerName: '# Unit Imped Maint',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The number of units with impedances exceeding Maintenance Limits',
		minWidth: 120,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => BuildDataGridCell(
			invalidValueCheck(params.value, '--'),
			'center',
			(params.value > 0) ? STATE_COLOR.MAINTENANCE : null,
			false
		),
	},
	{
		field: 'avg_imped_increase',
		headerName: 'Avg Imped Inc %',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The average impedance increase percentage across the system',
		minWidth: 100,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => BuildDataGridCell(invalidValueCheck(params.value, '--.--') + " %", 'right', null, false),
	},
	{
		field: 'avg_unit_in_service',
		headerName: 'Avg Unit Install Age',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The average number of years that a unit has been installed in the system',
		minWidth: 110,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => BuildDataGridCell(invalidValueCheck(params.value, '--.--') + " years", 'right', null, false),
	},
	{
		field: 'oldest_installation_date',
		headerName: 'Oldest Unit Install',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The date of the unit that has been installed in the system the longest',
		minWidth: 120,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => BuildDataGridCell(formatDate(params.value, '--/--/----'), 'right', null, false),
	},
	{
		field: 'avg_mfg_age',
		headerName: 'Avg Unit Mfg Age',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The average manufacture age of units in the system',
		minWidth: 110,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => BuildDataGridCell(invalidValueCheck(params.value, '--.--') + " years", 'right', null, false),
	},
	{
		field: 'oldest_mfg_date',
		headerName: 'Oldest Unit Mfg',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The oldest manufacture age of a unit in the system',
		minWidth: 120,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => BuildDataGridCell(formatDate(params.value, '--/--/----'), 'right', null, false),
	},
	{
		field: 'num_units_replaced',
		headerName: '# Units Replaced',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The number of units replaced in the system',
		minWidth: 100,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => BuildDataGridCell(invalidValueCheck(params.value, '--'), 'right', null, false),
	},
	{
		field: 'percent_units_replaced',
		headerName: '% Units Replaced',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The percentage of units replaced in the system',
		minWidth: 100,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => BuildDataGridCell(invalidValueCheck(params.value, '--.--') + " %", 'right', null, false),
	},
	{
		field: 'num_units_out_of_warranty',
		headerName: '# Units Past Warranty',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The total number of units that are past their warranty date in the system',
		minWidth: 100,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => BuildDataGridCell(invalidValueCheck(params.value, '--'), 'right', null, false),
	},
];

export default function LocationsTable() {

	const [loading, setLoading] = useState(true);
	const [locations, setLocations] = useState([]);
	const [rowCount, setRowCount] = useState(0);
	const [density, setDensity] = useState('compact');

	const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
	const [sortModel, setSortModel] = useState([{ field: 'location_name', sort: 'asc' }]);
	const [filterModel, setFilterModel] = useState({ items: [] });

	const navigate = useNavigate();

	useEffect(() => {
		setLoading(true);

		const sort = sortModel[0] || {};
		const filter = filterModel.items[0] || {};

		axiosInstance.get('/', {
			params: {
				page: paginationModel.page + 1, // backend pages usually 1-based
				page_size: paginationModel.pageSize,
				sort_field: sort.field,
				sort_order: sort.sort,
				filter_field: filter.field,
				filter_value: filter.value,
			}
		})
		.then(res => {
			setRowCount(res.data.count || 0);
			setLocations(res.data.locations || []);
		})
		.catch(error => {
			console.error("Error fetching locations:", error);
			setLocations([]);
			setRowCount(0);
		})
		.finally(() => {
			setLoading(false);
		});
	}, [paginationModel, sortModel, filterModel]);

	function AnalysisResultToStyle(analysisResult) {
		if (analysisResult) {
			switch (analysisResult) {
				case 0:  // ANALYSIS_UNKNOWN
				return 'unknown-color';
	
				case 1:  // ANALYSIS_WITHIN_LIMITS
					return 'withinLimits-color';
	
				case 2:  // ANALYSIS_ABOVE_LIMITS
					return 'maintenance-color';
	
				case 3:  // ANALYSIS_BELOW_LIMITS
					return 'maintenance-color';
	
				case 4:  // ANALYSIS_ABOVE_LIMITS_CRITICAL
					return 'critical-color';
	
				case 5:  // ANALYSIS_BELOW_LIMITS_CRITICAL
					return 'critical-color';
	
				case 6:  // ANALYSIS_OUT_OF_RANGE_CRITICAL
					return 'critical-color';
	
				case 7:  // ANALYSIS_TEMP_SENSOR_ABOVE_DIFF_LIMIT
					return 'critical-color';
	
				default:
					return 'unknown-color';
			}
		} else {
			return 'unknown-color';
		}
	}

	return (
		<DataGrid
			getRowId={(row) => row.location_id}
			rows={locations}
			columns={columns}
			loading={loading}
			rowCount={rowCount}
			pagination
			pageSizeOptions={[25, 50, 100]}
			paginationModel={paginationModel}
			onPaginationModelChange={(newModel) => setPaginationModel(newModel)}
			paginationMode="server"
			sortModel={sortModel}
			sortingMode="server"
			onSortModelChange={(newSortModel) => {
				setSortModel(newSortModel);
				setPaginationModel((prev) => ({ ...prev, page: 0 }));
			}}
			filterModel={filterModel}
			filterMode="server"
			onFilterModelChange={(newFilterModel) => {
				setFilterModel(newFilterModel);
				setPaginationModel((prev) => ({ ...prev, page: 0 }));
			}}
			density={density}
			onDensityChange={(newDensity) => setDensity(newDensity)}
			onRowClick={(rowParams) => {
				const locationId = rowParams.id;
				navigate(`/location/${locationId}`);
			}}
			getRowClassName={(row) => {
				return AnalysisResultToStyle(row.row.overall_system_analysis)
			}}
			slots={{
				toolbar: (() => (
					<GridToolbarContainer>
						<GridToolbarColumnsButton />
						<GridToolbarFilterButton />
						<GridToolbarDensitySelector />
						<GridToolbarExport />
					</GridToolbarContainer>
				)),
				noRowsOverlay: NoItemsOverlay,
				}}
			slotProps={{
				noRowsOverlay: { noItemsText: 'No Locations' },
			}}
			sx={{
				maxHeight: 'calc(100vh - 176px)',
				padding: 2,
			}}
		/>
	);
}